import { useMemo } from 'react';

import type {
  IntegrationRequestFormOptions,
  IntegrationRequestType,
} from '../../../models/IntegrationRequest';

export interface ExistingIntegrationInput {
  type: IntegrationRequestType;
  logisticOperatorId: string;
  serviceWithOperatorIds: string[];
  webhookUrl: string;
  webhookStatusIds: string[];
  currentRequestId?: string;
}

export interface ExistingIntegrationNotices {
  openRequests: IntegrationRequestFormOptions['openRequests'];
  integration: {
    code: string | null;
    currentServiceCodes: string[];
    removedServiceCodes: string[];
    activeWebhookCount: number;
  } | null;
  webhookOverlaps: { statusName: string; urls: string[] }[];
}

const useExistingIntegrationNotices = (
  options: IntegrationRequestFormOptions | undefined,
  input: ExistingIntegrationInput
): ExistingIntegrationNotices | null => {
  const {
    type,
    logisticOperatorId,
    serviceWithOperatorIds,
    webhookUrl,
    webhookStatusIds,
    currentRequestId,
  } = input;

  return useMemo(() => {
    if (!options) return null;

    const openRequests = options.openRequests.filter(
      (request) => request.id !== currentRequestId && request.type === type
    );

    const operator = options.logisticOperators.find((item) => item.id === logisticOperatorId);
    const currentServiceCodes = operator?.currentServiceCodes ?? [];
    const selectedCodes = (operator?.services ?? [])
      .filter((service) => serviceWithOperatorIds.includes(service.id))
      .map((service) => service.apiCode);
    const isIntegrated =
      options.commerce.integrated && (!operator || operator.currentServiceCodes !== null);

    const integration =
      type === 'API' && isIntegrated
        ? {
            code: options.commerce.code,
            currentServiceCodes,
            removedServiceCodes: selectedCodes.length
              ? currentServiceCodes.filter((code) => !selectedCodes.includes(code))
              : [],
            activeWebhookCount: new Set(options.activeWebhooks.map((webhook) => webhook.statusId))
              .size,
          }
        : null;

    const url = webhookUrl.trim();
    const webhookOverlaps = webhookStatusIds.flatMap((statusId) => {
      const urls = options.activeWebhooks
        .filter((webhook) => webhook.statusId === statusId)
        .flatMap((webhook) => webhook.urls)
        .filter((existing) => existing !== url);
      if (!urls.length) return [];
      const status = options.notifiableStatuses.find((item) => item.id === statusId);
      return [{ statusName: status?.commercialName || status?.name || statusId, urls }];
    });

    if (!openRequests.length && !integration && !webhookOverlaps.length) return null;
    return { openRequests, integration, webhookOverlaps };
  }, [
    options,
    type,
    logisticOperatorId,
    serviceWithOperatorIds,
    webhookUrl,
    webhookStatusIds,
    currentRequestId,
  ]);
};

export default useExistingIntegrationNotices;
